'use client';

import { AlertCircle } from 'lucide-react';

import { PolicyContent } from '@/components/agreements/PolicyContent';
import LoadingSpinner from '@/components/ui/loristry-spinner';
import { usePolicy } from '@/hooks/use-policy';

interface PolicyViewerProps {
  type: 'terms' | 'privacy';
  title: string;
}

export function PolicyViewer({ type, title }: PolicyViewerProps) {
  const { data: policy, loading, error } = usePolicy(type);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl mb-2">{title}</h1>
          {policy && (
            <p className="text-sm text-muted-foreground tracking-wide">
              Version {policy.version} • Effective{' '}
              {new Date(policy.effectiveDate).toLocaleDateString()}
            </p>
          )}
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size={48} text="Loading policy..." />
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <h3 className="mb-1">Failed to load policy</h3>
                <p className="text-sm text-muted-foreground">
                  {error.message}. Please try again later or contact support if the problem
                  persists.
                </p>
              </div>
            </div>
          </div>
        )}

        {policy && !loading && !error && <PolicyContent content={policy.content} />}
      </div>
    </div>
  );
}
